<mat-card
  class="default has-progress-bar"
  [class.warn]="warning$ | async"
>
  <mat-progress-bar [mode]="(progress$ | async) ? 'indeterminate' : 'determinate'"></mat-progress-bar>
  <mat-card-title fxLayout="row" fxLayoutAlign="space-between center">
    <span>{{ 'patient-details.intake-card.title' | translate }}</span>
    <div *ngIf="(data.appointment$ | async)?.intakeProgress" class="text-sm">
      {{ (data.appointment$ | async).intakeProgress || 0 }}% {{ 'common.complete' | translate }}
    </div>
    <div
      fxLayout="row"
      fxLayoutAlign="end center"
      *ngIf="data.appointment$ | async as appointment"
      fxLayoutGap="1rem"
    >
      <ng-container *ngIf="data.intake$ | async as intake; else startIntakeButton">
        <ng-container *appHasPermission="'appointment.intake.edit'">
          <button *ngIf="intake && !embedded; else startIntakeButton"
            mat-stroked-button
            [routerLink]="[
              '/patient',
              appointment.patientId,
              'appointment',
              appointment.id,
              'intake',
              appointment.intakeId
            ]"
          >
            <mat-icon>edit</mat-icon>
            <span>{{
              'patient-details.intake-card.button-review' | translate
              }}</span>
          </button>
        </ng-container>

        <button *ngIf="intake" mat-stroked-button [matMenuTriggerFor]="intakeDownloadMenu">
          <mat-icon>print</mat-icon>
          <span>{{
            'patient-details.intake-card.button-print' | translate
          }}</span>
        </button>
      </ng-container>

      <ng-template #startIntakeButton>
        <button *ngIf="!embedded" mat-stroked-button (click)="addIntake$.next()" [disabled]="progress$ | async">
          <mat-icon>add</mat-icon>
          <span>{{
            'patient-details.intake-card.button-fill-out' | translate
            }}</span>
        </button>
      </ng-template>

      <ng-container *appHasPermission="'appointment.intake.edit'">
        <button
          mat-stroked-button
          *ngIf="canSendIntakeLinkToPatient$ | async"
          [matMenuTriggerFor]="intakeSendMenu"
          matTooltip="Send message to patient asking them to complete intake forms"
          matTooltipPosition="above"
        >
          <mat-icon>send</mat-icon>
          <span>{{ 'patient-details.intake-card.button-send' | translate }}</span>
        </button>
      </ng-container>
    </div>
  </mat-card-title>

  <mat-menu #intakeDownloadMenu="matMenu">
    <ng-container *ngIf="data.intake$ | async as intake">
      <button
        mat-menu-item
        (click)="download(intake, $event, 'combined')"
        matTooltip="Registration, Questionnaire, Consents, and Patient-Uploaded Photos"
      >
        {{ 'patient-details.intake-card.menu.combined-with-photos' | translate }}
      </button>
      <button
        mat-menu-item
        (click)="download(intake,$event, 'combined-without-photos')"
        matTooltip="Registration, Questionnaire &amp; Consents"
      >
        {{
          'patient-details.intake-card.menu.combined-without-photos' | translate
        }}
      </button>
      <button mat-menu-item (click)="download(intake, $event, 'intake')">
        {{
          'patient-details.intake-card.menu.registration-questionnaire'
            | translate
        }}
      </button>
      <button mat-menu-item (click)="download(intake, $event, 'registration')">
        {{ 'patient-details.intake-card.menu.registration' | translate }}
      </button>
      <button mat-menu-item (click)="download(intake, $event, 'questionnaire')">
        {{ 'patient-details.intake-card.menu.questionnaire' | translate }}
      </button>
      <button
        mat-menu-item
        *ngFor="let documentType of customDocumentTypes$ | async"
        (click)="download(intake, $event, 'custom.' + documentType)"
      >
        {{ documentType }}
      </button>
      <button
        mat-menu-item
        (click)="download(intake, $event, 'combined-consents')"
        *ngIf="intake?.consents && intake?.consents.length > 1"
      >
        {{ 'patient-details.intake-card.menu.combined-consents' | translate }}
      </button>
      <button mat-menu-item (click)="download(intake, $event, 'insurance-card')">
        {{ 'patient-intake.print-type.insurance-cards' | translate }}
      </button>
      <button mat-menu-item (click)="download(intake, $event, 'identification')">
        {{ 'patient-intake.print-type.identification' | translate }}
      </button>
      <ng-container *ngIf="intake?.consents && intake?.consents.length > 0">
        <button
          mat-menu-item
          *ngFor="let consent of intake.consents"
          (click)="download(intake, $event, 'consent.' + consent.id)"
          [matTooltip]="consent.title"
        >
          {{ 'patient-details.intake-card.menu.consent' | translate }}:
          {{ consent.title }}
        </button>
      </ng-container>
      <ng-container *ngIf="imageQuestions$ | async  as imageQuestions">
        <button
          mat-menu-item
          *ngFor="let imageQuestion of imageQuestions"
          (click)="downloadImage(imageQuestion)"
        >
          {{ 'patient-details.intake-card.menu.image' | translate }}:
          {{ getQuestionField(imageQuestion.id, 'label') }}
        </button>
      </ng-container>
      <button
        *ngFor="let pdfDocument of pdfDocuments$ | async"
        mat-menu-item
        (click)="downloadPDFDocument(pdfDocument)"
      >
        {{ pdfDocument.sectionLabel }}
      </button>
    </ng-container>
  </mat-menu>

  <mat-menu #intakeSendMenu="matMenu">
    <ng-container *ngIf="data.patient$ | async as patient">
      <button mat-menu-item [disabled]="!patient?.phone" (click)="sendMessage$.next('sms')">
        <mat-icon>textsms</mat-icon>
        {{ 'common.send-via.text' | translate }}
      </button>
      <button mat-menu-item [disabled]="!patient?.email" (click)="sendMessage$.next('email')">
        <mat-icon>email</mat-icon>
        {{ 'common.send-via.email' | translate }}
      </button>
    </ng-container>
  </mat-menu>

  <ng-template #noIntake>
    <ng-container *ngIf="data.appointment$ | async as appointment">
      <form
        *ngIf="intakes$ | async as intakes; else emptyCard"
        (ngSubmit)="onIntakeSelectionSubmit(appointment.id)"
      >
        <mat-card-content fxLayout="column">
          <p>{{ 'patient-details.intake-card.no-intake-info' | translate }}</p>

          <div class="text-warn" *ngIf="appointment?.intakeStatus === 'N'">
            {{ 'patient-details.intake-card.no-intake-delivered' | translate }}
          </div>
          <div *ngIf="appointment?.intakeStatus === 'D'">
            {{ 'patient-details.intake-card.intake-delivered' | translate }}
          </div>

          <mat-form-field class="mt-2">
            <mat-label>{{
              'patient-details.intake-card.existing-form' | translate
            }}</mat-label>
            <mat-select [formControl]="intakeControl">
              <mat-option [value]="undefined">{{
                'common.none' | translate
              }}</mat-option>
              <mat-option *ngFor="let intake of intakes" [value]="intake.id">
                {{ 'patient-details.intake-card.intake-from' | translate }}
                {{
                  intake.updatedAt ? intake.updatedAt.format('MM-DD-YYYY') : ''
                }}
              </mat-option>
            </mat-select>
            <mat-hint>{{
              'patient-details.intake-card.intake-from-hint' | translate
            }}</mat-hint>
          </mat-form-field>
        </mat-card-content>

        <mat-card-actions fxLayout="row" fxLayoutAlign="end center">
          <button
            mat-stroked-button
            type="submit"
            [disabled]="intakeControl.value == null"
          >
            {{
              'patient-details.intake-card.intake-link-appointment' | translate
            }}
          </button>
        </mat-card-actions>
      </form>
    </ng-container>

    <ng-template #emptyCard>
      <mat-card-content>
        <p>
          {{ 'patient-details.intake-card.no-intake-documents' | translate }}
        </p>
        <div class="text-warn" *ngIf="(data.appointment$ | async)?.intakeStatus === 'N'">
          {{ 'patient-details.intake-card.no-intake-delivered' | translate }}
        </div>
        <div *ngIf="(data.appointment$ | async)?.intakeStatus === 'D'">
          {{ 'patient-details.intake-card.intake-delivered' | translate }}
        </div>
      </mat-card-content>
    </ng-template>
  </ng-template>

  <mat-card-content *ngIf="data.intake$ | async as intake; else noIntake">
    <ng-container *ngIf="warning$ | async">
      <p class="text-warn" *ngIf="hasUpdatedPatientName$ | async">
        {{ 'patient-details.intake-card.has-updated-name' | translate }}
      </p>
      <p class="text-warn" *ngIf="hasUpdatedPatientDOB$ | async">
        {{ 'patient-details.intake-card.has-updated-dob' | translate }}
      </p>
      <p class="text-warn" *ngIf="hasUpdatedPatientSex$ | async">
        {{ 'patient-details.intake-card.has-updated-sex' | translate }}
      </p>
      <p class="text-warn">
        {{ 'patient-details.intake-card.intake-info-update' | translate }}
      </p>
    </ng-container>
    <div fxLayout="row wrap" fxLayoutGap="1rem" class="contactInfo">
      <section>
        <header class="text-primary uppercase text-xs mb-3">
          {{ 'patient-details.intake-card.contact-information' | translate }}
        </header>
        <div class="detail field mb-2">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-contact-information.name'
                | translate
            }}
          </div>
          <mmx-copy-text class="value"
            >{{ intake.givenName?.toUpperCase() }}
            {{ intake.familyName?.toUpperCase() }}</mmx-copy-text
          >
        </div>
        <div class="detail field mb-2" *ngIf="intake.address">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-contact-information.address'
                | translate
            }}
          </div>
          <mmx-copy-text
            class="value"
            [innerHTML]="intake.address | address"
          ></mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.phoneNumber">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-contact-information.phone'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.phoneNumber | phone
          }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.email">
          <div class="label text-secondary uppercase text-xs">
            {{ 'patient-details.intake-card.section-contact-information.email' | translate }}
          </div>
          <mmx-copy-text class="value">{{ intake.email }}</mmx-copy-text>
        </div>
      </section>

      <section>
        <header class="text-primary uppercase text-xs mb-3">
          {{ 'patient-details.intake-card.demographics' | translate }}
        </header>
        <div class="detail field mb-2" *ngIf="intake.dob">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-demographics.dob' | translate
            }}
          </div>
          <mmx-copy-text
            class="value"
            [copyValue]="intake.dob.format('MM/DD/YYYY')"
            >{{ intake.dob.format('LL') }}</mmx-copy-text
          >
        </div>
        <div class="detail field mb-2" *ngIf="intake.weight">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-demographics.weight'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{ intake.weight }} lbs</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.height">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-demographics.height'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{ intake.height }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.sex">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-demographics.sex' | translate
            }}
          </div>
          <mmx-copy-text class="value">{{ intake.sex }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.gender">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-demographics.gender'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{ intake.gender }}</mmx-copy-text>
        </div>
      </section>

      <section *ngIf="intake.emergencyContactName">
        <header class="text-primary uppercase text-xs mb-3">
          {{ 'patient-details.intake-card.emergency-contact' | translate }}
        </header>
        <div class="detail field mb-2">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-emergency-contact.name'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.emergencyContactName.toUpperCase()
          }}</mmx-copy-text>
        </div>
        <div
          class="detail field mb-2"
          *ngIf="intake.emergencyContactPhoneNumber"
        >
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-emergency-contact.phone'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.emergencyContactPhoneNumber | phone
          }}</mmx-copy-text>
        </div>
      </section>

      <section *ngIf="intake.hasPCP">
        <header class="text-primary uppercase text-xs mb-3">
          {{ 'patient-details.intake-card.pcp' | translate }}
        </header>
        <div class="detail field mb-2">
          <div class="label text-secondary uppercase text-xs">
            {{ 'patient-details.intake-card.section-pcp.name' | translate }}
          </div>
          <mmx-copy-text class="value">{{
            intake.pcpName.toUpperCase()
          }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.pcpOffice">
          <div class="label text-secondary uppercase text-xs">
            {{ 'patient-details.intake-card.section-pcp.office' | translate }}
          </div>
          <mmx-copy-text class="value">{{ intake.pcpOffice }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.pcpPhoneNumber">
          <div class="label text-secondary uppercase text-xs">
            {{ 'patient-details.intake-card.section-pcp.phone' | translate }}
          </div>
          <mmx-copy-text class="value">{{
            intake.pcpPhoneNumber | phone
          }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.pcpAddress">
          <div class="label text-secondary uppercase text-xs">
            {{ 'patient-details.intake-card.section-pcp.address' | translate }}
          </div>
          <mmx-copy-text
            class="value"
            [innerHTML]="intake.pcpAddress | address"
          ></mmx-copy-text>
        </div>
      </section>

      <section *ngIf="intake.hasGuarantor">
        <header class="text-primary uppercase text-xs mb-3">
          {{ 'patient-details.intake-card.guarantor' | translate }}
        </header>
        <div class="detail field mb-2">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-guarantor.name' | translate
            }}
          </div>
          <mmx-copy-text class="value"
            >{{ (intake.guarantorGivenName || '').toUpperCase() }}
            {{
              (intake.guarantorFamilyName || '').toUpperCase()
            }}</mmx-copy-text
          >
        </div>
        <div class="detail field mb-2" *ngIf="intake.guarantorDOB">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-guarantor.dob' | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.guarantorDOB.format('LL')
          }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.guarantorPhoneNumber">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-guarantor.phone' | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.guarantorPhoneNumber | phone
          }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.guarantorRelationship">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-guarantor.relationship'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.guarantorRelationship
          }}</mmx-copy-text>
        </div>
      </section>

      <section *ngIf="intake.hasInsurance">
        <header class="text-primary uppercase text-xs mb-3">
          {{ 'patient-details.intake-card.insurance' | translate }}
        </header>
        <div class="detail field mb-2" *ngIf="intake.insuranceCarrier">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-insurance.carrier'
                | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.insuranceProvider
              ? intake.insuranceProvider.name
              : intake.insuranceCarrier
          }}</mmx-copy-text>
        </div>
        <div class="detail field mb-2" *ngIf="intake.insuranceNumber">
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-insurance.member' | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.insuranceNumber
          }}</mmx-copy-text>
        </div>
        <div
          class="detail field mb-2"
          *ngIf="
            intake.isPrimaryOnInsurance === true ||
            intake.isPrimaryOnInsurance === false
          "
        >
          <div class="label text-secondary uppercase text-xs">
            {{
              'patient-details.intake-card.section-insurance.policy' | translate
            }}
          </div>
          <mmx-copy-text class="value">{{
            intake.isPrimaryOnInsurance ? 'Yes' : 'No'
          }}</mmx-copy-text>
        </div>
      </section>

      <section *ngFor="let section of sections$ | async">
        <div
          class="section"
          *ngIf="
            !section.default &&
            !section.isHidden &&
            section.questions.length > 0
          "
        >
          <header class="text-primary uppercase text-xs mb-3">
            {{ section.label }}
          </header>
          <div
            class="detail field mb-2"
            *ngFor="let question of section.questions"
          >
            <mmx-custom-question
              [question]="question"
              [intake]="intake"
            ></mmx-custom-question>
          </div>
        </div>
      </section>
    </div>

    <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px">
      <ng-container *ngIf="uploadedPhotos$ | async as photos">
        <div
          fxFlex="50px"
          class="text-center"
          *ngFor="let photo of photos"
          fxLayout="row"
          fxLayoutAlign="center center"
        >
          <img
            (click)="viewFile(photo)"
            width="50px"
            [src]="photo.thumbnailUrl || photo.signedUrl"
          />
        </div>
      </ng-container>
      <ng-container *ngIf="uploadedDocuments$ | async as files">
        <span *ngFor="let file of files">
          <mat-icon (click)="viewFile(file)" [matTooltip]="file.documentTitle"
            >article</mat-icon
          >
        </span>
      </ng-container>
      <span *ngIf="filesLoadError" class="text-warn">
        {{ 'patient-details.intake-card.failed-checking-files' | translate }}
      </span>
    </div>
  </mat-card-content>
</mat-card>
